import { DialogContent, styled, useTheme } from '@mui/material';
import { InlineHelperText } from 'FeatureModules/ProductPage/Components/InlineHelperText/InlineHelperText';
import useDialog from 'hooks/useDialog';
import { DialogDefault } from 'DesignSystem';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import React, { FC } from 'react';
import {
  IChainActivity,
  IChainStep,
  ISubChain,
} from 'FeatureModules/ComponentChain/types/chain.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import SeparatorLine from 'Components/SeparatorLine';

interface IActivitySelectDecisionDialogProps extends IDefaultDialogProps {
  selectedStepId: string;
  steps: IChainStep[];
  activities: IChainActivity[];
  subChains: ISubChain[];
  partner?: IBasePartnerCompany;
  onActivityAdd: (
    activityId: string,
    activity?: Pick<IChainActivity, 'chainStepId' | 'incomingLinks'>
  ) => void;
}

const HelperTextWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 3),
}));

const ActivitySelectDecisionDialog: FC<IActivitySelectDecisionDialogProps> = ({
  open,
  selectedStepId,
  steps,
  activities,
  subChains,
  partner,
  onActivityAdd,
  onClose,
}) => {
  const { openDialog } = useDialog();
  const theme = useTheme();

  const handleSelectExistingActivity = () => {
    openDialog({
      type: 'SELECT_EXISTING_ACTIVITY',
      props: { ownedById: partner?.id, onActivityAdd },
    });
    onClose?.();
  };

  const handleAddNewActivity = () => {
    openDialog({
      type: 'ADD_EDIT_ACTIVITY',
      props: {
        mode: 'add-chain-activity',
        partner,
        hidePartnerSelector: !!partner,
        selectedStepId,
        steps,
        subChains,
        activities,
        onActivityAdd,
      },
    });
    onClose?.();
  };

  return (
    <DialogDefault
      open={open}
      title="Add Activity"
      onClose={onClose}
      maxWidth="xs"
      data-testid="decision-dialog"
    >
      <DialogContent>
        <ContentWrapper>
          <ThemeButton
            color="PRIMARY"
            size="large"
            fullWidth
            data-testid="select-partner-button"
            onClick={handleSelectExistingActivity}
          >
            Select existing activity
          </ThemeButton>
          <SeparatorLine label="or" width="80%" backgroundColor={theme.custom.colors.offWhite} />
          <ThemeButton
            color="BLUE_ICE"
            size="large"
            fullWidth
            data-testid="add-new-activity-button"
            onClick={handleAddNewActivity}
          >
            Add new
          </ThemeButton>

          <HelperTextWrapper>
            <InlineHelperText
              variant="INFO"
              helperText="You can connect a new activity to an existing partner."
            />
          </HelperTextWrapper>
        </ContentWrapper>
      </DialogContent>
    </DialogDefault>
  );
};

export default ActivitySelectDecisionDialog;
